import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface VisionBoard {
  id: string;
  title: string;
  description?: string;
  affirmations: string[];
  action_items: { text: string; completed: boolean }[];
  images: { url: string; caption?: string }[];
  layout_type: string;
  is_favorite: boolean;
}

interface VisionBoardDisplayProps {
  board: VisionBoard;
}

export default function VisionBoardDisplay({ board }: VisionBoardDisplayProps) {
  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
          {board.title}
        </h1>
        {board.description && (
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {board.description}
          </p>
        )}
      </div>

      {/* Image Grid */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {board.images.map((image, index) => (
              <div key={index} className="space-y-2">
                <div className="relative aspect-square overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                  <img
                    src={image.url}
                    alt={image.caption || `Vision ${index + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                {image.caption && (
                  <p className="text-sm text-center text-muted-foreground italic">
                    {image.caption}
                  </p>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Affirmations */}
        {board.affirmations.length > 0 && (
          <Card>
            <CardContent className="p-6 space-y-4">
              <h2 className="text-2xl font-semibold text-primary">
                ✨ Affirmations
              </h2>
              <div className="space-y-3">
                {board.affirmations.map((affirmation, index) => (
                  <div
                    key={index}
                    className="p-4 rounded-lg bg-primary/5 border border-primary/20 hover:bg-primary/10 transition-colors"
                  >
                    <p className="text-foreground italic">"{affirmation}"</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Items */}
        {board.action_items.length > 0 && (
          <Card>
            <CardContent className="p-6 space-y-4">
              <h2 className="text-2xl font-semibold text-primary">
                🎯 Action Steps
              </h2>
              <div className="space-y-3">
                {board.action_items.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-4 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors"
                  >
                    <Badge variant="outline" className="mt-1">
                      {index + 1}
                    </Badge>
                    <p className="text-foreground flex-1">{item.text}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Inspirational Footer */}
      <Card className="bg-gradient-to-r from-primary/10 to-purple-600/10 border-primary/20">
        <CardContent className="p-8 text-center">
          <p className="text-lg font-medium text-foreground">
            Your vision is the blueprint for your reality. 
            <br />
            <span className="text-primary">
              Visualise it. Believe it. Achieve it.
            </span>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
